import React, { useState, useEffect } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Table, Input, Label, Row, Col, Progress } from "reactstrap";
import axios from "axios";
import Swal from "sweetalert2";
import * as XLSX from "xlsx";
import ExcelJS from "exceljs";

// Alta masiva de modelos desde un Excel (layout), portado de AmericanFly.
// El layout se descarga aquí mismo: las columnas que vienen de otro catálogo
// (línea, marca, familia, colección, composición, proveedor, empresa) son
// listas desplegables con los datos vivos del sistema, solo seleccionables.
//
// Reglas: CODIGO, NOMBRE, PIEZAS POR CAJA y los 6 catálogos son obligatorios
// (el validador de Sauver los exige y colección/composición no tienen "NA").
// Un código que ya existe (o repetido en el archivo) no se da de alta.
// EMPRESA vacía = la única empresa activa. Cada modelo nace con su inventario
// en CEDIS en 0, igual que el alta normal (ArticulosCreate).
const headers = () => ({ Authorization: `Bearer: ${localStorage.getItem("app_token")}` });
const env = (k) => process.env[k];
const txt = (v) => String(v === undefined || v === null ? "" : v).trim();
const nu = (v) => Number(String(v ?? "").replace(/[$,\s]/g, "")) || 0;
const norm = (v) => txt(v).toUpperCase().replace(/\s+/g, " ");

// Catálogos que van como lista desplegable: [columna, clave en cats, campo del nombre]
const CATALOGOS = [
  ["LINEA", "lineas", "name"],
  ["MARCA", "marcas", "name"],
  ["FAMILIA", "familias", "name"],
  ["COLECCION", "colecciones", "name"],
  ["COMPOSICION", "composiciones", "name"],
  ["PROVEEDOR", "proveedores", "nombre_comercial"],
  ["EMPRESA", "empresas", "name"],
];

// Columnas del layout: [encabezado, obligatorio?, tipo]
const COLUMNAS = [
  ["CODIGO", true, "texto"],
  ["NOMBRE", true, "texto"],
  ["DESCRIPCION", false, "texto"],
  ["LINEA", true, "catalogo"],
  ["MARCA", true, "catalogo"],
  ["FAMILIA", true, "catalogo"],
  ["COLECCION", true, "catalogo"],
  ["COMPOSICION", true, "catalogo"],
  ["PROVEEDOR", true, "catalogo"],
  ["EMPRESA", false, "catalogo"],
  ["ANO", false, "texto"],
  ["PIEZAS POR CAJA", true, "numero"],
  ["PARES POR PACK", false, "numero"],
  ["COSTO USD", false, "numero"],
  ["COSTO MX", false, "numero"],
  ["VENTA", false, "numero"],
  ["PRECIO TIENDA", false, "numero"],
  ["EAN", false, "texto"],
  ["CODIGO SAT", false, "texto"],
  ["OBSERVACIONES", false, "texto"],
];
const FILAS_LAYOUT = 500;
const letra = (n) => {
  let s = "";
  for (n += 1; n > 0; n = Math.floor((n - 1) / 26)) s = String.fromCharCode(65 + ((n - 1) % 26)) + s;
  return s;
};

function ImportarArticulosModal({ abierto, cerrar, onTerminado, userId }) {
  const [cats, setCats] = useState({});
  const [filas, setFilas] = useState([]);
  const [archivo, setArchivo] = useState("");
  const [guardando, setGuardando] = useState(false);
  const [avance, setAvance] = useState(0);

  useEffect(() => {
    if (!abierto) return;
    setFilas([]); setArchivo(""); setAvance(0); setCats({});
    const pide = (url) => axios.get(url, { headers: headers() }).then((r) => r.data || []).catch(() => []);
    Promise.all([
      pide(env("REACT_APP_URL_ARTICULOS")),
      pide(env("REACT_APP_URL_LINEAS")),
      pide(env("REACT_APP_URL_MARCAS")),
      pide(env("REACT_APP_URL_FAMILIAS")),
      pide(env("REACT_APP_URL_COLECCIONES")),
      pide(env("REACT_APP_URL_COMPOSICIONES")),
      pide(env("REACT_APP_URL_PROVEEDORES")),
      pide(env("REACT_APP_URL_EMPRESAS")),
    ]).then(([articulos, lineas, marcas, familias, colecciones, composiciones, proveedores, empresas]) =>
      setCats({ articulos, lineas, marcas, familias, colecciones, composiciones, proveedores, empresas })
    );
  }, [abierto]);

  const activos = (lista) => (lista || []).filter((x) => x.is_active !== "No");
  // Busca en un catálogo activo por nombre (ignora mayúsculas y espacios de más)
  const busca = (lista, valor, campo = "name") => {
    const t = norm(valor);
    if (!t) return null;
    return activos(lista).find((x) => norm(x[campo]) === t) || null;
  };
  const nombresDe = (clave, campo) =>
    activos(cats[clave]).map((x) => txt(x[campo])).filter(Boolean).sort((a, b) => a.localeCompare(b));

  async function descargaLayout() {
    const wb = new ExcelJS.Workbook();
    const hoja = wb.addWorksheet("Articulos"); // debe ser la 1a hoja: es la que se lee al subir
    const hojaCat = wb.addWorksheet("Catalogos", { state: "hidden" });
    const hojaInst = wb.addWorksheet("Instrucciones");

    hoja.columns = COLUMNAS.map(([c, ob, tipo]) => ({
      header: ob ? `${c} *` : c,
      key: c,
      width: tipo === "catalogo" ? 28 : Math.max(14, c.length + 4),
      // Códigos, EAN y año como texto para que Excel no los convierta
      style: tipo === "texto" ? { numFmt: "@" } : {},
    }));
    hoja.getRow(1).font = { bold: true };
    hoja.views = [{ state: "frozen", ySplit: 1 }];

    // Hoja oculta con las listas vivas; cada catálogo en su columna
    CATALOGOS.forEach(([col, clave, campo], i) => {
      const valores = nombresDe(clave, campo);
      hojaCat.getCell(1, i + 1).value = col;
      valores.forEach((v, j) => (hojaCat.getCell(j + 2, i + 1).value = v));
      const colLayout = COLUMNAS.findIndex(([c]) => c === col);
      if (!valores.length) return;
      const rango = `Catalogos!$${letra(i)}$2:$${letra(i)}$${valores.length + 1}`;
      for (let r = 2; r <= FILAS_LAYOUT + 1; r++) {
        hoja.getCell(r, colLayout + 1).dataValidation = {
          type: "list",
          allowBlank: true,
          formulae: [rango],
          showErrorMessage: true,
          errorTitle: `${col} no válido`,
          error: `Elige un valor de la lista de ${col}.`,
        };
      }
    });

    // Columnas numéricas: solo números >= 0
    COLUMNAS.forEach(([c, , tipo], i) => {
      if (tipo !== "numero") return;
      for (let r = 2; r <= FILAS_LAYOUT + 1; r++) {
        hoja.getCell(r, i + 1).dataValidation = {
          type: "decimal",
          operator: "greaterThanOrEqual",
          allowBlank: true,
          formulae: [0],
          showErrorMessage: true,
          error: `${c} debe ser un número.`,
        };
      }
    });

    const empresasAct = activos(cats.empresas);
    [
      ["COMO LLENAR ESTE LAYOUT"],
      [],
      ["1", "Captura un modelo por renglón, desde el renglón 2 de la hoja Articulos."],
      ["2", "Las columnas con * son obligatorias: CODIGO, NOMBRE, PIEZAS POR CAJA, LINEA, MARCA, FAMILIA, COLECCION, COMPOSICION y PROVEEDOR."],
      ["3", "LINEA, MARCA, FAMILIA, COLECCION, COMPOSICION, PROVEEDOR y EMPRESA se eligen de la lista desplegable de cada celda (vienen del sistema)."],
      ["4", "Si falta un valor en una lista, primero dalo de alta en su catálogo del sistema y vuelve a descargar el layout."],
      ["5", "El CODIGO no se puede repetir: si ya existe en el sistema o se repite en el archivo, ese renglón no se da de alta."],
      ["6", empresasAct.length === 1 ? `EMPRESA vacía = ${empresasAct[0].name}.` : "EMPRESA es obligatoria."],
      ["7", "Importes, PIEZAS POR CAJA y PARES POR PACK van en número, sin signo de pesos; vacíos = 0 (PARES POR PACK vacío = 1)."],
      ["8", "CODIGO SAT: clave de 8 dígitos (ejemplo 53102303). Sin código SAT el modelo no se puede facturar."],
      ["9", "Los textos vacíos (descripción, año, EAN, observaciones) se guardan como NA. No cambies los nombres de las columnas."],
    ].forEach((r) => hojaInst.addRow(r));
    hojaInst.getColumn(1).width = 6;
    hojaInst.getColumn(2).width = 120;
    hojaInst.getRow(1).font = { bold: true };

    const buffer = await wb.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "LayoutAltaArticulos.xlsx";
    link.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  function leeArchivo(file) {
    if (!file) return;
    setArchivo(file.name); setAvance(0);
    const lector = new FileReader();
    lector.onload = (e) => {
      try {
        const wb = XLSX.read(e.target.result, { type: "array" });
        const hoja = wb.Sheets[wb.SheetNames[0]];
        const crudas = XLSX.utils.sheet_to_json(hoja, { defval: "" });
        setFilas(revisa(crudas));
      } catch (err) {
        Swal.fire({ icon: "error", title: "No se pudo leer el archivo", text: "Usa el layout que se descarga de este mismo botón." });
      }
    };
    lector.readAsArrayBuffer(file);
  }

  // Valida cada renglón y deja listo el cuerpo que se va a mandar
  function revisa(crudas) {
    const dame = (f, col) => {
      const k = Object.keys(f).find((x) => norm(x).replace(/\*/g, "").trim() === col);
      return k ? txt(f[k]) : "";
    };
    const existentes = new Set((cats.articulos || []).map((a) => norm(a.codigo)));
    const vistos = new Set();
    const empresasAct = activos(cats.empresas);

    return crudas.map((f, i) => {
      const codigo = dame(f, "CODIGO");
      const nombre = dame(f, "NOMBRE");
      const fila = { renglon: i + 2, codigo, nombre };
      if (!codigo && !nombre) return { ...fila, estado: "vacio" };

      const faltan = ["CODIGO", "NOMBRE", "PIEZAS POR CAJA", "LINEA", "MARCA", "FAMILIA", "COLECCION", "COMPOSICION", "PROVEEDOR"]
        .filter((c) => !dame(f, c));
      if (faltan.length) return { ...fila, estado: "error", detalle: `Falta ${faltan.join(", ")}` };
      if (existentes.has(norm(codigo))) return { ...fila, estado: "duplicado", detalle: "Ya existe en el sistema" };
      if (vistos.has(norm(codigo))) return { ...fila, estado: "duplicado", detalle: "Repetido en el archivo" };

      const noExisten = [];
      const ids = {};
      CATALOGOS.forEach(([col, clave, campo]) => {
        let valor = dame(f, col);
        if (col === "EMPRESA" && !valor && empresasAct.length === 1) valor = empresasAct[0].name;
        const x = busca(cats[clave], valor, campo);
        if (!x) noExisten.push(`${col} "${valor}"`);
        else ids[clave] = x._id;
      });
      if (noExisten.length) return { ...fila, estado: "error", detalle: `No existe ${noExisten.join(", ")}` };

      const avisos = [];
      const satTxt = dame(f, "CODIGO SAT").replace(/\s/g, "");
      if (satTxt && !/^\d{8}$/.test(satTxt)) {
        return { ...fila, estado: "error", detalle: `CODIGO SAT "${satTxt}" inválido: deben ser 8 dígitos` };
      }
      if (!satTxt) avisos.push("sin CODIGO SAT (no se podrá facturar)");

      vistos.add(norm(codigo));
      const oNAtxt = (col) => dame(f, col) || "NA";
      // Solo campos que acepta validators/ArticulosValidator.create
      const cuerpo = {
        codigo,
        nombre,
        descripcion: oNAtxt("DESCRIPCION"),
        piezasPorCaja: nu(dame(f, "PIEZAS POR CAJA")),
        paresPorPack: nu(dame(f, "PARES POR PACK")) || 1,
        costoUSD: nu(dame(f, "COSTO USD")),
        costoMX: nu(dame(f, "COSTO MX")),
        venta: nu(dame(f, "VENTA")),
        precioTienda: nu(dame(f, "PRECIO TIENDA")),
        observaciones: oNAtxt("OBSERVACIONES"),
        ano: oNAtxt("ANO"),
        EAN: oNAtxt("EAN"),
        codigoSAT: satTxt || "NA",
        codigoUnidad: "NA",
        lineas: ids.lineas,
        marcas: ids.marcas,
        familias: ids.familias,
        colecciones: ids.colecciones,
        composiciones: ids.composiciones,
        proveedores: ids.proveedores,
        empresas: ids.empresas,
        pesoCaja: 0,
        tamanoCaja: "NA",
        vendedores: "Si",
        colores: [],
        tallas: [],
      };
      return { ...fila, estado: "nuevo", cuerpo, detalle: avisos.join(" · ") };
    }).filter((f) => f.estado !== "vacio");
  }

  const nuevos = filas.filter((f) => f.estado === "nuevo");
  const duplicados = filas.filter((f) => f.estado === "duplicado");
  const errores = filas.filter((f) => f.estado === "error");
  const listo = !!cats.lineas;

  async function guardar() {
    if (!nuevos.length) return;
    const ok = await Swal.fire({
      icon: "question",
      title: `Se darán de alta ${nuevos.length} modelo(s)`,
      html: duplicados.length || errores.length
        ? `<b style="color:#b45309">${duplicados.length} duplicado(s) y ${errores.length} con error se omiten.</b>`
        : "",
      showCancelButton: true,
      confirmButtonText: "Sí, dar de alta",
      cancelButtonText: "Cancelar",
    });
    if (!ok.isConfirmed && !ok.value) return;
    setGuardando(true);
    const fallaron = [];
    for (let i = 0; i < nuevos.length; i++) {
      try {
        const r = await axios.post(env("REACT_APP_URL_ARTICULOS"), nuevos[i].cuerpo, { headers: headers() });
        // Su renglón de inventario en CEDIS, igual que el alta de un modelo
        await axios.post(
          env("REACT_APP_URL_INVENTARIOS"),
          { articulos: r.data._id, areas: env("REACT_APP_AREAS_CEDIS") },
          { headers: headers() }
        );
      } catch (error) {
        fallaron.push(`${nuevos[i].codigo}: ${error.response?.data?.message || error.message}`);
      }
      setAvance(Math.round(((i + 1) / nuevos.length) * 100));
    }
    try {
      await axios.post(
        env("REACT_APP_URL_LOGS"),
        { tipo: "Alta masiva modelos", detalle: `${nuevos.length - fallaron.length} modelos desde layout`, user: userId },
        { headers: headers() }
      );
    } catch (e) { /* el log no detiene el alta */ }
    setGuardando(false);
    await Swal.fire({
      icon: fallaron.length ? "warning" : "success",
      title: `${nuevos.length - fallaron.length} modelo(s) dados de alta`,
      html: fallaron.length ? `No se pudieron dar de alta:<br><small>${fallaron.slice(0, 10).join("<br>")}</small>` : "",
    });
    cerrar();
    if (onTerminado) onTerminado();
  }

  const color = { nuevo: "#15803d", duplicado: "#b45309", error: "#dc2626" };
  return (
    <Modal size="lg" isOpen={abierto} toggle={cerrar}>
      <ModalHeader toggle={cerrar}>Alta de modelos desde layout</ModalHeader>
      <ModalBody>
        <Row>
          <Col md={5}>
            <Label><b>1. Layout</b></Label>
            <div>
              <Button size="sm" color="info" onClick={descargaLayout} disabled={!listo}>
                <i className="far fa-file-excel" /> Descargar layout
              </Button>
            </div>
            <small style={{ color: "#64748b" }}>
              {listo ? "Los catálogos vienen como listas desplegables del sistema." : "Cargando catálogos..."}
            </small>
          </Col>
          <Col md={7}>
            <Label><b>2. Sube el layout lleno</b></Label>
            <Input type="file" accept=".xlsx,.xls" disabled={!listo} onChange={(e) => leeArchivo(e.target.files[0])} />
            {archivo ? <small style={{ color: "#64748b" }}>{archivo}</small> : null}
          </Col>
        </Row>
        <br />
        {filas.length ? (
          <>
            <div style={{ display: "flex", gap: 14, marginBottom: 6, fontSize: 13 }}>
              <span style={{ color: color.nuevo, fontWeight: 700 }}>{nuevos.length} por dar de alta</span>
              <span style={{ color: color.duplicado, fontWeight: 700 }}>{duplicados.length} duplicados</span>
              <span style={{ color: color.error, fontWeight: 700 }}>{errores.length} con error</span>
            </div>
            <div style={{ maxHeight: 320, overflowY: "auto" }}>
              <Table size="sm" bordered>
                <thead>
                  <tr><th>Renglón</th><th>Código</th><th>Nombre</th><th>Estado</th></tr>
                </thead>
                <tbody>
                  {filas.map((f) => (
                    <tr key={f.renglon}>
                      <td>{f.renglon}</td>
                      <td>{f.codigo}</td>
                      <td>{f.nombre}</td>
                      <td style={{ color: color[f.estado], fontWeight: 600 }}>
                        {f.estado === "nuevo" ? (f.detalle ? `Se dará de alta · ${f.detalle}` : "Se dará de alta") : f.detalle}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </>
        ) : (
          <p style={{ color: "#9ca3af" }}>Sube el layout para ver qué se va a dar de alta.</p>
        )}
        {guardando ? <Progress animated value={avance}>{avance}%</Progress> : null}
      </ModalBody>
      <ModalFooter>
        <Button color="success" onClick={guardar} disabled={guardando || !nuevos.length}>
          {guardando ? "Dando de alta..." : `Dar de alta ${nuevos.length} modelo(s)`}
        </Button>
        <Button color="secondary" onClick={cerrar} disabled={guardando}>Cerrar</Button>
      </ModalFooter>
    </Modal>
  );
}

export default ImportarArticulosModal;
